import React, { useMemo } from 'react';

import colors from 'styles/theme/colors';

const DEFAULT_DURATION = 1500;
const DEFAULT_MIN_OPACITY = 0.25;

export type AIOlioLoaderProps = {
  width?: number;
  height?: number;
  duration?: number;
  minOpacity?: number;
};

/**
 * Generates keyframe JSON for a sequence x1, x2, ..., xn, xn-1, ..., x1
 * @param n - The number of elements in the sequence
 * @param minOpacity - The minimum opacity of the elements
 * @param prefix - The prefix of the keyframes
 * @returns The keyframes
 */
function generateKeyframes(n: number, minOpacity: number = 0.25, prefix: string = 'repeatX') {
  if (n < 2) throw new Error('n must be at least 2');
  const steps = 2 * n - 2;
  const percentStep = 100 / steps;
  const result: Record<string, { percent: number; opacity: number }[]> = {};

  for (let i = 0; i < n; i++) {
    const key = `${prefix}${i}`;
    const frames: { percent: number; opacity: number }[] = [];
    for (let s = 0; s <= steps; s++) {
      // The element that is lit at this step
      let litIdx: number;
      if (s < n) {
        litIdx = s;
      } else {
        litIdx = 2 * n - 2 - s;
      }
      const percent = Math.round(s * percentStep);
      frames.push({
        percent,
        opacity: litIdx === i ? 1 : minOpacity,
      });
    }
    result[key] = frames;
  }
  return result;
}

export default function AIOlioLoader({
  width = 79,
  height = 24,
  duration = DEFAULT_DURATION,
  minOpacity = DEFAULT_MIN_OPACITY,
}: AIOlioLoaderProps) {
  const keyframes = useMemo(() => {
    const keyframes = generateKeyframes(4, minOpacity, 'olioRepeat');
    const keyframesString = Object.entries(keyframes)
      .map(([key, frames]) => {
        return `
        @keyframes ${key} {
          ${frames
            .map(
              (frame) => `
            ${frame.percent}% { opacity: ${frame.opacity}; }
          `
            )
            .join('\n')}
        }
      `;
      })
      .join('\n');
    return keyframesString;
  }, [minOpacity]);

  return (
    <svg xmlns='http://www.w3.org/2000/svg' width={width} height={height} viewBox='0 0 79 24' fill='none'>
      <style>
        {`
          ${keyframes}

          #olio-o1 {
            opacity: ${minOpacity};
            animation: olioRepeat0 ${duration}ms linear infinite;
          }
          #olio-l {
            opacity: ${minOpacity};
            animation: olioRepeat1 ${duration}ms linear infinite;
          }
          #olio-i {
            opacity: ${minOpacity};
            animation: olioRepeat2 ${duration}ms linear infinite;
          }
          #olio-o2 {
            opacity: ${minOpacity};
            animation: olioRepeat3 ${duration}ms linear infinite;
          }
        `}
      </style>
      <path
        id='olio-o1'
        fill='url(#o1)'
        d='M16.991.898C15.491.302 13.856 0 12.132 0 10.41 0 8.774.302 7.275.898a11.402 11.402 0 0 0-3.86 2.52C1.213 5.608 0 8.657 0 12c0 3.343 1.213 6.39 3.415 8.583a11.4 11.4 0 0 0 3.86 2.519c1.5.596 3.134.898 4.857.898 1.724 0 3.359-.302 4.86-.898 1.469-.583 2.769-1.43 3.864-2.518 2.207-2.194 3.423-5.242 3.423-8.584 0-3.342-1.215-6.39-3.423-8.584A11.427 11.427 0 0 0 16.991.898Zm-4.859 16.79c-1.577 0-2.937-.537-3.93-1.551C7.19 15.104 6.655 13.673 6.655 12c0-1.673.535-3.104 1.547-4.137.993-1.014 2.353-1.55 3.93-1.55 1.583 0 2.944.536 3.936 1.55 1.008 1.03 1.541 2.462 1.541 4.137 0 1.675-.533 3.106-1.541 4.137-.992 1.014-2.353 1.55-3.936 1.55Z'
      />
      <path id='olio-l' fill='url(#l)' d='M32.582.388h-6.476v23.223h17.529v-6.208H32.582V.388Z' />
      <path id='olio-i' fill='url(#i)' d='M52.028.388h-6.476v23.223h6.476V.388Z' />
      <path
        id='olio-o2'
        fill='url(#o2)'
        d='M74.633 3.417A11.426 11.426 0 0 0 70.77.897C69.268.303 67.633 0 65.909 0c-1.723 0-3.357.302-4.857.898a11.403 11.403 0 0 0-3.86 2.52C54.99 5.608 53.777 8.657 53.777 12c0 3.343 1.213 6.39 3.415 8.583a11.4 11.4 0 0 0 3.86 2.519c1.5.596 3.134.898 4.858.898 1.723 0 3.358-.302 4.859-.898 1.47-.583 2.77-1.43 3.864-2.518 2.208-2.194 3.424-5.242 3.424-8.584 0-3.342-1.216-6.39-3.424-8.584Zm-8.723 14.27c-1.578 0-2.937-.536-3.93-1.55-1.013-1.033-1.547-2.464-1.547-4.137 0-1.673.534-3.104 1.546-4.137.994-1.014 2.353-1.55 3.93-1.55 1.583 0 2.944.536 3.936 1.55 1.008 1.03 1.541 2.462 1.541 4.137 0 1.675-.532 3.106-1.54 4.137-.993 1.014-2.354 1.55-3.936 1.55Z'
      />
      <defs>
        <linearGradient id='o1' x1='0' x2='24.279' y1='12' y2='12' gradientUnits='userSpaceOnUse'>
          <stop stopColor='#6B45B8' stopOpacity='.75' />
          <stop offset='.486' stopColor='#6241CD' stopOpacity='.871' />
          <stop offset='1' stopColor={colors.aiPurple} />
        </linearGradient>
        <linearGradient id='l' x1='26.106' x2='43.635' y1='12' y2='12' gradientUnits='userSpaceOnUse'>
          <stop stopColor='#6B45B8' stopOpacity='.75' />
          <stop offset='.486' stopColor='#6241CD' stopOpacity='.871' />
          <stop offset='1' stopColor={colors.aiPurple} />
        </linearGradient>
        <linearGradient id='i' x1='45.552' x2='52.028' y1='12' y2='12' gradientUnits='userSpaceOnUse'>
          <stop stopColor='#6B45B8' stopOpacity='.75' />
          <stop offset='.486' stopColor='#6241CD' stopOpacity='.871' />
          <stop offset='1' stopColor={colors.aiPurple} />
        </linearGradient>
        <linearGradient id='o2' x1='53.777' x2='78.057' y1='12' y2='12' gradientUnits='userSpaceOnUse'>
          <stop stopColor='#6B45B8' stopOpacity='.75' />
          <stop offset='.486' stopColor='#6241CD' stopOpacity='.871' />
          <stop offset='1' stopColor={colors.aiPurple} />
        </linearGradient>
      </defs>
    </svg>
  );
}
